import { Filters } from "@/types/hotel";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface Room {
  id: number;
  count: number;
}

export interface RoomDataType {
  adults: number;
  children: number[];
}

interface ModalsControllersState {
  showFavouredHotelsModal: boolean;
  setShowFavouredModal: (hotelNearbyFilter: boolean) => void;
}

interface HotelsCartState {
  rooms: Room[];
  passengerName: string;
  bookerName: string;
  passengerEmail: string;
  phone: string;
  saveDetails: boolean;
  reciveOffer: boolean;
  roomData: RoomDataType[];
  roomKey: string;
  currentPage: number | null;
  total: number;
  filters: Filters;
  favoritedHotels: string[];
  hotelNearbyFilter: string;

  setHotelNearbyFilter: (hotelNearbyFilter: string) => void;
  addFavoriteHotel: (hotelId: string) => void;
  removeFavoriteHotel: (hotelId: string) => void;
  setFilters: (by: Filters) => void;
  setRoomData: (by: RoomDataType[]) => void;
  setRooms: (by: Room[]) => void;
  setName: (by: string) => void;
  setBookerName: (by: string) => void;
  setEmail: (by: string) => void;
  setPhone: (by: string) => void;
  setSaveDetails: () => void;
  setReciveOffer: () => void;
  setRoomKey: (by: string) => void;
  addChild: (roomIndex: number) => void;
  removeChild: (roomIndex: number) => void;
  removeAdult: (roomIndex: number) => void;
  addRoom: () => void;
  removeRoom: (roomIndex: number) => void;
  addAdult: (roomIndex: number) => void;
  setCurrentPage: (page: number) => void;
  setTotal: (total: number) => void;

  editChildAge: (
    roomIndex: number,
    childIndex: number,
    newNumber: number,
  ) => void;
}

export const useModalsControllersStore = create<ModalsControllersState>()(
  (set) => ({
    showFavouredHotelsModal: false,
    setShowFavouredModal: (showFavouredHotelsModal: boolean) =>
      set({ showFavouredHotelsModal }),
  }),
);

export const useHotelsCardStore = create<HotelsCartState>()(
  persist(
    (set) => ({
      rooms: [],

      hotelNearbyFilter: "restaurant",
      setHotelNearbyFilter: (hotelNearbyFilter: string) =>
        set({ hotelNearbyFilter }),
      favoritedHotels: [],
      // other state variables and methods...
      addFavoriteHotel: (hotelId: string) => {
        set((state: HotelsCartState) => ({
          ...state,
          favoritedHotels: [...state.favoritedHotels, hotelId],
        }));
      },

      removeFavoriteHotel: (hotelId: string) => {
        set((state) => ({
          favoritedHotels: state.favoritedHotels.filter((id) => id !== hotelId),
        }));
      },
      // other state variables and methods...

      passengerName: "",
      currentPage: null,
      perPage: 10,
      bookerName: "",
      passengerEmail: "",
      phone: "",
      saveDetails: false,
      reciveOffer: false,
      filters: {
        maxPrice: 1000,
        minPrice: 0,
        accommodations: [],
        boards: [],
        categories: [],
        facilities: [],
        segments: [],
      },
      roomData: [],
      roomKey: "",
      total: 0,
      setFilters: (filters) => set({ filters }),
      setTotal: (total) => set({ total }),
      setCurrentPage: (currentPage) => set({ currentPage }),
      setRoomKey: (roomKey) => set({ roomKey }),
      setRoomData: (roomData: RoomDataType[]) => set((state) => ({ roomData })),
      addRoom: () => {
        set((state) => ({
          roomData: [...state.roomData, { adults: 1, children: [] }],
        }));
      },
      removeRoom: (roomIndex: number) => {
        set((state) => {
          let newArray = state.roomData;
          newArray.splice(roomIndex, 1);
          return { roomData: newArray };
        });
      },
      addChild: (roomIndex: number) => {
        set((state) => {
          const updatedRoomData = [...state.roomData]; // Create a copy of the roomData array
          const children = [...updatedRoomData[roomIndex].children, 2]; // Add 0 to the children array for the specific index
          updatedRoomData[roomIndex] = {
            ...updatedRoomData[roomIndex],
            children,
          }; // Update the children array in the specific index
          return { roomData: updatedRoomData }; // Return the updated roomData array
        });
      },
      removeChild: (roomIndex: number) => {
        set((state) => {
          const updatedRoomData = [...state.roomData]; // Create a copy of the roomData array
          const children = [...updatedRoomData[roomIndex].children]; // Create a copy of the children array for the specific index
          if (children.length > 0) {
            children.pop(); // Reduce the children array by one item
          }
          updatedRoomData[roomIndex] = {
            ...updatedRoomData[roomIndex],
            children,
          }; // Update the children array in the specific index
          return { roomData: updatedRoomData }; // Return the updated roomData array
        });
      },
      addAdult: (roomIndex: number) => {
        set((state) => {
          const updatedRoomData = [...state.roomData]; // Create a copy of the roomData array
          updatedRoomData[roomIndex] = {
            ...updatedRoomData[roomIndex],
            adults: updatedRoomData[roomIndex].adults + 1,
          };
          return { roomData: updatedRoomData }; // Return the updated roomData array
        });
      },
      removeAdult: (roomIndex: number) => {
        set((state: HotelsCartState) => {
          const updatedRoomData = [...state.roomData]; // Create a copy of the roomData array
          const currentAdults = updatedRoomData[roomIndex].adults;
          if (currentAdults > 0) {
            updatedRoomData[roomIndex] = {
              ...updatedRoomData[roomIndex],
              adults: currentAdults - 1,
            }; // Reduce one unit from adults for the specific index
          }
          return { roomData: updatedRoomData }; // Return the updated roomData array
        });
      },
      editChildAge: (
        roomIndex: number,
        childIndex: number,
        newNumber: number,
      ) => {
        set((state: HotelsCartState) => {
          const updatedRoomData = [...state.roomData]; // Create a copy of the roomData array
          const children = [...updatedRoomData[roomIndex].children]; // Create a copy of the children array for the specific room index
          children[childIndex] = newNumber; // Update the child number at the specified index
          updatedRoomData[roomIndex] = {
            ...updatedRoomData[roomIndex],
            children,
          };
          return { roomData: updatedRoomData }; // Return the updated roomData array
        });
      },
      setRooms: (rooms) => set({ rooms }),
      setName: (passengerName) => set({ passengerName }),
      setBookerName: (bookerName) => set({ bookerName }),
      setEmail: (passengerEmail) => set({ passengerEmail }),
      setPhone: (phone) => set({ phone }),
      setSaveDetails: () =>
        set((state) => ({ saveDetails: !state.saveDetails })),
      setReciveOffer: () =>
        set((state) => ({ reciveOffer: !state.reciveOffer })),
    }),
    { name: "hotelCard" },
  ),
);
